import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Popover from "@/components/ui/popover";
import {
  Actions as ActionsIcon,
  Eye as EyeIcon,
  User as UserIcon,
  Edit as EditIcon,
  EmployeeAdd as EmployeeAddIcon,
  Shield as ShieldCheckIcon,
  Password as PasswordIcon,
} from "@/components/icons";
import type { FilterField, TableColumn, SortOption } from "@/types/component";
import type { Staff } from "../types/staff-management.types";
import { staffManagementApi } from "../services/staff-management.api";
import { rolesAndPermissionsApi } from "@/features/roles-and-permissions/services/roles-and-permissions.api";
import TrainingAssignmentModal from "../components/TrainingAssignmentModal";
import PersonnelSummaryDrawer from "../components/PersonnelSummaryDrawer";
import EditStaffModal from "../components/EditStaffModal";
import AssignRoleModal from "../components/AssignRoleModal";
import AddStaffModal from "../components/AddStaffModal";
import BulkImportStaffModal from "../components/BulkImportStaffModal";
import ChangeStaffPasswordModal from "../components/ChangeStaffPasswordModal";
import { updateAdminUserPassword } from "@/lib/users.api";
import Toast from "@/components/ui/toast";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import { getRoleDisplayName } from "@/features/roles-and-permissions/config/role-display-names";
import { UserRoleType } from "@/constants/auth";
import { isCentralManagerSession } from "@/core/auth/utils/session-role-types";
import styles from "./StaffManagementPage.module.scss";

const StaffManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.STAFF_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.STAFF_UPDATE);
  const canAssignRole = hasPermission(PERMISSIONS.STAFF_ASSIGN_ROLE);
  const actorIsCentralManager = useMemo(() => isCentralManagerSession(), []);
  // Şifre değiştirme artık kendi USERS.UPDATE_PASSWORD iznine bağlı (önceden
  // USERS.UPDATE ile birlikte geliyordu + hardcoded EDUCATION_MANAGER check).
  // Backend aynı izni doğruluyor (PUT /users/:id/password).
  const canChangeStaffPassword = hasPermission(PERMISSIONS.STAFF_UPDATE_PASSWORD);

  const [staff, setStaff] = useState<Staff[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRows, setSelectedRows] = useState<Staff[]>([]);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {},
  );
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [roleOptions, setRoleOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [rootDepartmentOptions, setRootDepartmentOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [isTrainingAssignmentModalOpen, setIsTrainingAssignmentModalOpen] =
    useState(false);
  const [personnelSummaryStaff, setPersonnelSummaryStaff] =
    useState<Staff | null>(null);
  const [editStaff, setEditStaff] = useState<Staff | null>(null);
  const [assignRoleStaff, setAssignRoleStaff] = useState<Staff | null>(null);
  const [changePasswordStaff, setChangePasswordStaff] = useState<Staff | null>(
    null,
  );
  const [isAddStaffModalOpen, setIsAddStaffModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [toastState, setToastState] = useState<{
    variant: "success" | "fail";
    title?: string;
    message: string;
  } | null>(null);

  // Debounce search (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Rol ve kök birim listeleri (id = value, backend'e id gider)
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [rolesRes, rootDepartments] = await Promise.all([
          rolesAndPermissionsApi.getAll({ pageSize: 100 }),
          staffManagementApi.getRootDepartments(),
        ]);
        setRoleOptions(
          rolesRes.data.map((r) => ({
            label: getRoleDisplayName(r.roleName) || r.roleName,
            value: r.id,
          })),
        );
        setRootDepartmentOptions(
          rootDepartments.map((d) => ({ label: d.name, value: d.id })),
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error loading filter options:", error);
      }
    };
    loadOptions();
  }, []);

  const fetchStaff = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await staffManagementApi.getAll({
        page: currentPage,
        pageSize,
        filters: {
          search: debouncedSearch.trim() || undefined,
          status: filterValues.status,
          roleId: filterValues.roleId,
          rootDepartmentId: filterValues.rootDepartmentId,
          title: filterValues.title?.trim() || undefined,
          tcNo: filterValues.tcNo?.trim() || undefined,
          name: filterValues.name?.trim() || undefined,
        },
        sortBy: filterValues.sortBy ?? "createdAt",
        sortOrder: filterValues.sortOrder ?? "desc",
      });
      setStaff(response.data);
      setTotalRecords(response.total);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching staff:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearch, filterValues]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleRowSelect = (row: Staff) => {
    setSelectedRows((prev) => {
      const isSelected = prev.some((r) => r.id === row.id);
      if (isSelected) {
        return prev.filter((r) => r.id !== row.id);
      }
      return [...prev, row];
    });
  };

  const handleSelectAll = (checked: boolean) => {
    const eligibleOnPage = staff.filter((r) => !r.isBanned);
    if (checked) {
      setSelectedRows((prev) => {
        const ids = new Set(prev.map((r) => r.id));
        const toAdd = eligibleOnPage.filter((r) => !ids.has(r.id));
        return [...prev, ...toAdd];
      });
    } else {
      const pageIds = new Set(eligibleOnPage.map((r) => r.id));
      setSelectedRows((prev) => prev.filter((r) => !pageIds.has(r.id)));
    }
  };

  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [rowId]: isOpen }));
  };

  const handleSaveToEducation = () => {
    const eligible = selectedRows.filter((s) => !s.isBanned);
    if (eligible.length === 0) {
      setToastState({
        variant: "fail",
        message: t("staffManagement.trainingAssignment.selectPersonnelWarning"),
      });
      return;
    }
    setSelectedRows(eligible);
    setIsTrainingAssignmentModalOpen(true);
  };

  const handleTrainingAssignmentConfirm = (
    _staffIds: string[],
    _trainingId: string,
  ) => {
    setSelectedRows([]);
    setToastState({
      variant: "success",
      message: t("staffManagement.trainingAssignment.toast.enrollSuccess"),
    });
  };

  const handleEnrollSingleStaff = (staff: Staff) => {
    if (staff.isBanned) return;
    setSelectedRows([staff]);
    setIsTrainingAssignmentModalOpen(true);
  };

  const handleFilterChange = (filters: Record<string, any>) => {
    setFilterValues(filters);
  };

  const sortOptions: SortOption[] = [
    { label: t("staffManagement.sort.newest"), value: "newest" },
    { label: t("staffManagement.sort.oldest"), value: "oldest" },
    {
      label: t("staffManagement.sort.nameAsc"),
      value: "nameAsc",
    },
    {
      label: t("staffManagement.sort.nameDesc"),
      value: "nameDesc",
    },
  ];

  const handleSortChange = (option: SortOption) => {
    const sortMap: Record<
      string,
      { sortBy: string; sortOrder: "asc" | "desc" }
    > = {
      newest: { sortBy: "createdAt", sortOrder: "desc" },
      oldest: { sortBy: "createdAt", sortOrder: "asc" },
      nameAsc: { sortBy: "firstName", sortOrder: "asc" },
      nameDesc: { sortBy: "firstName", sortOrder: "desc" },
    };
    const next = sortMap[option?.value ?? "newest"] ?? sortMap.newest;
    setFilterValues((prev) => ({ ...prev, ...next }));
    setCurrentPage(1);
  };

  const addMenuOptions =
    canCreate || canUpdate
      ? [
          ...(canCreate
            ? [
                {
                  label: t("staffManagement.addButton"),
                  onClick: () => setIsAddStaffModalOpen(true),
                },
                {
                  label: t("staffManagement.bulkImportButton"),
                  onClick: () => setIsBulkImportModalOpen(true),
                },
              ]
            : []),
          ...(canUpdate
            ? [
                {
                  label: t("staffManagement.saveToEducationButton"),
                  onClick: handleSaveToEducation,
                },
              ]
            : []),
        ]
      : undefined;

  const filterFields: FilterField[] = [
    {
      name: "tcNo",
      label: t("staffManagement.filters.tcNo"),
      type: "text",
      placeholder: t("staffManagement.filters.tcNoPlaceholder"),
    },
    {
      name: "name",
      label: t("staffManagement.filters.name"),
      type: "text",
      placeholder: t("staffManagement.filters.namePlaceholder"),
    },
    {
      name: "title",
      label: t("staffManagement.filters.title"),
      type: "text",
      placeholder: t("staffManagement.filters.titlePlaceholder"),
    },
    {
      name: "roleId",
      label: t("staffManagement.filters.role"),
      type: "dropdown",
      placeholder: t("staffManagement.filters.rolePlaceholder"),
      options: roleOptions,
      searchable: true,
      multiple: false,
    },
    {
      name: "rootDepartmentId",
      label: t("staffManagement.filters.rootDepartment"),
      type: "dropdown",
      placeholder: t("staffManagement.filters.rootDepartmentPlaceholder"),
      options: rootDepartmentOptions,
      searchable: true,
      multiple: false,
    },
  ];

  const columns: TableColumn<Staff>[] = [
    {
      header: t("staffManagement.columns.staffId"),
      accessor: "staffId",
      className: "staff-id-column",
    },
    {
      header: t("staffManagement.columns.name"),
      accessor: "name",
      className: "staff-name-column",
    },
    {
      header: t("staffManagement.columns.title"),
      accessor: "title",
      className: "title-column",
    },
    {
      header: t("staffManagement.columns.role"),
      accessor: (row) => getRoleDisplayName(row.roleName) || "—",
      className: "role-column",
    },
    {
      header: t("staffManagement.columns.department"),
      accessor: (row) => row.rootDepartment ?? row.department ?? "—",
      className: "department-column",
    },
    {
      header: t("staffManagement.columns.completedEducationsCount"),
      accessor: (row) => (row.completedEducationsCount ?? 0).toString(),
      className: "completed-trainings-column",
    },
  ];

  const renderActions = (row: Staff) => {
    const rowId = row.id;
    const isOpen = openDropdowns[rowId] || false;

    const isBanned = row.isBanned === true;

    const menuItems = [
      {
        label: t("staffManagement.actions.personnelSummary"),
        icon: <EyeIcon width={18} height={18} />,
        onClick: () => {
          handleDropdownToggle(rowId, false);
          setPersonnelSummaryStaff(row);
        },
      },
      ...(canChangeStaffPassword
        ? [
            {
              label: t("staffManagement.actions.changePassword"),
              icon: <PasswordIcon width={18} height={18} />,
              disabled: isBanned,
              onClick: () => {
                handleDropdownToggle(rowId, false);
                setChangePasswordStaff(row);
              },
            },
          ]
        : []),
      {
        label: t("staffManagement.actions.personnelDetail"),
        icon: <UserIcon width={18} height={18} />,
        onClick: () => {
          handleDropdownToggle(rowId, false);
          navigate(`/dashboard/staff-management/${row.id}`);
        },
      },
      ...(canUpdate
        ? [
            {
              label: t("staffManagement.actions.edit"),
              icon: <EditIcon width={18} height={18} />,
              onClick: () => {
                handleDropdownToggle(rowId, false);
                setEditStaff(row);
              },
            },
          ]
        : []),
      // Sistem Yöneticisinin rollerine yalnızca Sistem Yöneticisi dokunabilir;
      // diğer yetkililere bu satırda aksiyon hiç gösterilmez.
      ...(canAssignRole &&
      (actorIsCentralManager ||
        !row.roleTypes?.includes(UserRoleType.CENTRAL_MANAGER))
        ? [
            {
              label: t("staffManagement.actions.assignRole"),
              icon: <ShieldCheckIcon width={18} height={18} />,
              onClick: () => {
                handleDropdownToggle(rowId, false);
                setAssignRoleStaff(row);
              },
            },
          ]
        : []),
      {
        label: t("staffManagement.actions.enrollInTraining"),
        icon: <EmployeeAddIcon width={18} height={18} />,
        disabled: isBanned,
        onClick: () => {
          handleDropdownToggle(rowId, false);
          handleEnrollSingleStaff(row);
        },
      },
    ];

    const menuContent = (
      <div className={styles.actionMenu}>
        {menuItems.map((item, index) => (
          <button
            key={index}
            className={`${styles.actionMenuItem} ${
              item.disabled ? styles.actionMenuItemDisabled : ""
            }`}
            disabled={item.disabled}
            onClick={(e) => {
              e.stopPropagation();
              if (item.disabled) return;
              item.onClick();
            }}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>
    );

    return (
      <Popover
        content={menuContent}
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(rowId, false)}
        position="bottom"
      >
        <button
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  return (
    <DashboardLayout>
      {toastState && (
        <div className={styles.toastWrapper}>
          <Toast
            variant={toastState.variant}
            message={toastState.message}
            title={toastState.title}
            onClose={() => setToastState(null)}
            duration={5000}
          />
        </div>
      )}
      <Header title={t("staffManagement.title")} />

      <PageHeader
        searchPlaceholder={t("staffManagement.searchPlaceholder")}
        searchValue={searchTerm}
        onSearch={handleSearch}
        showSort
        sortOptions={sortOptions}
        onSort={handleSortChange}
        filters={filterFields}
        onFilterChange={handleFilterChange}
        onAddNew={
          !addMenuOptions
            ? canCreate
              ? () => setIsAddStaffModalOpen(true)
              : canUpdate
                ? handleSaveToEducation
                : undefined
            : undefined
        }
        addButtonLabel={
          canCreate
            ? t("staffManagement.addButton")
            : canUpdate
              ? t("staffManagement.saveToEducationButton")
              : undefined
        }
        addMenuOptions={addMenuOptions}
      />

      <AddStaffModal
        isOpen={isAddStaffModalOpen}
        onClose={() => setIsAddStaffModalOpen(false)}
        onSuccess={() => {
          fetchStaff();
          setToastState({
            variant: "success",
            message: t("staffManagement.addModal.toastSuccess"),
          });
        }}
      />

      <BulkImportStaffModal
        isOpen={isBulkImportModalOpen}
        onClose={() => setIsBulkImportModalOpen(false)}
        onSuccess={() => {
          fetchStaff();
          setToastState({
            variant: "success",
            message: t("staffManagement.bulkImport.toastSuccess"),
          });
        }}
      />

      <TrainingAssignmentModal
        isOpen={isTrainingAssignmentModalOpen}
        onClose={() => setIsTrainingAssignmentModalOpen(false)}
        selectedStaff={selectedRows}
        onConfirm={handleTrainingAssignmentConfirm}
      />

      <PersonnelSummaryDrawer
        isOpen={!!personnelSummaryStaff}
        onClose={() => setPersonnelSummaryStaff(null)}
        staff={personnelSummaryStaff}
        onGoToDetail={(staff) => {
          setPersonnelSummaryStaff(null);
          navigate(`/dashboard/staff-management/${staff.id}`);
        }}
        onEdit={
          canUpdate
            ? (staff) => {
                setPersonnelSummaryStaff(null);
                setEditStaff(staff);
              }
            : undefined
        }
        onEnrollInTraining={
          canUpdate
            ? (staff) => {
                setPersonnelSummaryStaff(null);
                handleEnrollSingleStaff(staff);
              }
            : undefined
        }
      />

      <EditStaffModal
        isOpen={!!editStaff}
        onClose={() => setEditStaff(null)}
        staff={editStaff}
        canEditPassword={canChangeStaffPassword}
        onConfirm={async (staffId, data) => {
          try {
            const [firstName, ...rest] = (data.name || "").trim().split(/\s+/);
            const lastName = rest.join(" ") || "";
            const normalizePhone = (raw: string): string | undefined => {
              const trimmed = raw.trim();
              if (!trimmed) return undefined;
              // Var olan auth util'inin davranışına benzer şekilde TR numarayı E.164'e çevir
              const digits = trimmed.replace(/\D/g, "");
              if (digits.length === 10 && digits.startsWith("5")) {
                return `+90${digits}`;
              }
              if (digits.length === 11 && digits.startsWith("90")) {
                return `+${digits}`;
              }
              if (trimmed.startsWith("+90") || trimmed.startsWith("90")) {
                return trimmed.startsWith("+") ? trimmed : `+${trimmed}`;
              }
              return trimmed.startsWith("+") ? trimmed : `+90${digits}`;
            };

            await staffManagementApi.update(staffId, {
              firstName: firstName || undefined,
              lastName: lastName || undefined,
              email: data.email || undefined,
              identityNumber: data.tcId || undefined,
              phoneNumber: normalizePhone(data.phone) || undefined,
              title: data.title?.trim() || undefined,
              sicilNo: data.sicilNo?.trim() || undefined,
              ldapUsername: data.ldapUsername?.trim() || undefined,
              locationId: data.locationId || undefined,
              departmentId: data.departmentId || undefined,
              gender: data.gender || undefined,
            });

            // Şifre opsiyonel: doluysa adanmış endpoint üzerinden güncelle.
            // Backend update DTO password kabul etmediği için ayrı çağrı yapılır.
            const newPassword = data.password?.trim();
            if (newPassword && canChangeStaffPassword) {
              await updateAdminUserPassword(staffId, { password: newPassword });
            }

            setEditStaff(null);
            fetchStaff();
          } catch {
            // Error already shown by apiClient interceptor
          }
        }}
      />

      <AssignRoleModal
        isOpen={!!assignRoleStaff}
        onClose={() => setAssignRoleStaff(null)}
        staff={assignRoleStaff}
        onConfirm={() => {
          setAssignRoleStaff(null);
          fetchStaff();
          setToastState({
            variant: "success",
            message: t("staffManagement.assignRoleModal.toastSuccess"),
          });
        }}
      />

      <ChangeStaffPasswordModal
        isOpen={!!changePasswordStaff}
        onClose={() => setChangePasswordStaff(null)}
        staff={changePasswordStaff}
        onSuccess={() => {
          setToastState({
            variant: "success",
            message: t("staffManagement.changePasswordModal.toastSuccess"),
          });
        }}
      />

      <Table
        columns={columns}
        data={staff}
        isLoading={isLoading}
        selectable
        isRowSelectable={(row) => !row.isBanned}
        selectedRows={selectedRows}
        onRowSelect={handleRowSelect}
        onSelectAll={handleSelectAll}
        getRowId={(row) => row.id}
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        renderActions={renderActions}
        openDropdowns={openDropdowns}
        emptyMessage={t("staffManagement.noData")}
      />
    </DashboardLayout>
  );
};

export default StaffManagementPage;
